import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

/**
 * Le etichette d'ambito, in italiano. È l'unica traduzione che vive nel frontend: i codici
 * arrivano dal backend (`ScopeLevel.code()`), che resta la sorgente dei valori ammessi.
 *
 * Sono le stesse parole del selettore d'ambito nella scheda Ruoli
 * (`components/roles/PermissionGrid.js`), e devono restare tali: la stessa cosa chiamata in
 * due modi in due schermate della stessa applicazione è un difetto, non una sfumatura.
 */
const ETICHETTE_AMBITO = {
    organization: 'Organizzazione',
    group: 'Gruppo',
    own: 'Propri',
};

/**
 * I permessi di **un** gruppo di risorse (spec §10.6), una riga per risorsa.
 *
 * Non è una matrice risorse × azioni: con sette azioni e la maggior parte delle celle vuote,
 * una tabella costringeva a leggere per esclusione. Qui ogni riga dice in positivo cosa si
 * può fare — una pillola per azione concessa, con il suo ambito dentro — e solo dopo cosa
 * non si può.
 *
 * Rende ciò che riceve e non ricompone la tassonomia: azioni, ambito e ruoli di provenienza
 * arrivano già calcolati dal backend, che è la sola sorgente della griglia. Una seconda copia
 * qui divergerebbe alla prima risorsa aggiunta, e divergerebbe in silenzio.
 *
 * Le tre condizioni di un'azione restano distinguibili, come §10.6 richiede:
 *   — **concessa**      → pillola, con l'ambito quando esiste
 *   — **non concessa**  → nominata dopo le pillole, in «non consentito: …»
 *   — **non prevista**  → assente del tutto. Il dominio non offre quell'operazione su quella
 *     risorsa: elencarla fra le non consentite direbbe il falso, perché non è un divieto ma
 *     un'operazione che non esiste. Un attributo estratto non si crea a mano.
 */
export default function PermissionMatrix({ resourceGroup }) {
    return (
        <Box>
            {resourceGroup.resources.map((risorsa, indice) => (
                <RigaRisorsa
                    key={risorsa.code}
                    risorsa={risorsa}
                    primaRiga={indice === 0}
                />
            ))}
        </Box>
    );
}

function RigaRisorsa({ risorsa, primaRiga }) {
    const concesse = risorsa.actions.filter((a) => a.granted);
    const negate = risorsa.actions.filter((a) => a.supported && !a.granted);

    return (
        <Box
            data-testid={`risorsa-${risorsa.code}`}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                py: 1.5,
                px: 2,
                borderTop: primaRiga ? 0 : 1,
                borderColor: 'divider',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
        >
            <Typography sx={{ minWidth: 200, flexShrink: 0 }}>{risorsa.label}</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                {concesse.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nessun accesso
                    </Typography>
                ) : (
                    concesse.map((azione) => (
                        <Chip
                            key={azione.code}
                            size="small"
                            data-testid={`azione-${risorsa.code}-${azione.code}`}
                            data-stato="concesso"
                            label={
                                azione.scope
                                    ? `${azione.label} · ${ETICHETTE_AMBITO[azione.scope] ?? azione.scope}`
                                    : azione.label
                            }
                            // `primary.light` e' la tinta al 10% definita in theme.js, che e'
                            // esattamente lo sfondo delle pillole nei mockup. Non usare una
                            // scala numerica tipo `primary.50`: questo tema non la definisce,
                            // MUI la passa a CSS come stringa letterale e la pillola resta
                            // senza sfondo, senza che nulla segnali l'errore.
                            sx={{
                                bgcolor: 'primary.light',
                                color: 'primary.main',
                                fontWeight: 400,
                            }}
                        />
                    ))
                )}

                {negate.length > 0 && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        data-testid={`non-consentito-${risorsa.code}`}
                    >
                        non consentito: {negate.map((a) => a.label).join(', ')}
                    </Typography>
                )}
            </Box>

            <Provenienza azioni={concesse} risorsa={risorsa.code} />
        </Box>
    );
}

/**
 * «da <ruolo> in <gruppo>», allineata a destra: la risposta a «perché posso farlo», che è la
 * ragione per cui questa pagina esiste.
 *
 * Il gruppo accompagna il ruolo dal modello a gruppi: un'azione ad ambito «Gruppo» vale sui
 * soli gruppi della concessione che la dà, e «da Operatore» non dice dove. Si legge
 * `sources`; `fromRoles` resta come ripiego per un backend che non lo porta ancora.
 *
 * Sta sulla riga e non sulla singola pillola perché quasi sempre le azioni di una risorsa
 * arrivano tutte dalla stessa concessione, e ripeterlo su ogni pillola coprirebbe
 * l'informazione con la sua stessa ripetizione. Quando le sorgenti sono più di una si elencano
 * tutte: è il caso interessante — «me lo dà ancora l'altro ruolo» — e va mostrato, non
 * riassunto.
 */
function Provenienza({ azioni, risorsa }) {
    if (azioni.length === 0) {
        return <Box sx={{ minWidth: 160, flexShrink: 0 }} />;
    }

    const stradeDi = (a) => (a.sources
        ? a.sources.map((s) => (s.group ? `${s.role} in ${s.group}` : `${s.role} su ogni gruppo`))
        : (a.fromRoles || []));

    // Concesso ma senza provenienza: il break-glass a tabelle vuote e un guasto di lettura con
    // cache calda ci arrivano entrambi (`rolesFor` degrada in silenzio, `scopesFor` no — vedi
    // il Javadoc di CachingPermissionResolver.rolesFor). Non si inventa un ruolo: si dice che
    // l'attribuzione non è disponibile, così questa pagina — il primo passo di diagnosi di un
    // accesso negato — non tace proprio quando chi la legge ne ha più bisogno.
    const senzaProvenienza = azioni.some((a) => stradeDi(a).length === 0);

    // Deduplica sulle stringhe solo per non ripetere la stessa strada tre volte di fila in
    // questa riga: quella che conta, per id, l'ha già fatta il backend.
    const strade = [];
    azioni.forEach((a) => stradeDi(a).forEach((s) => {
        if (!strade.includes(s)) strade.push(s);
    }));

    return (
        <Box sx={{ minWidth: 160, flexShrink: 0, textAlign: { xs: 'left', md: 'right' } }}>
            {senzaProvenienza && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                    data-testid={`provenienza-ignota-${risorsa}`}
                >
                    provenienza non determinabile
                </Typography>
            )}
            {strade.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                    da {strade.join(', ')}
                </Typography>
            )}
        </Box>
    );
}
